import _objectSpread from "/Users/natebosscher1/Sites/terrain-group/webapp/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { EventEmitter } from "nate-react-api-helpers";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export var PageRenderContext = /*#__PURE__*/ createContext({ AddPage: function () { return new Page(-2, function () { }, function () { }); } });
export function useSyncedRef(input) { var ref = useRef(input); ref.current = input; return ref; }
export function usePageInstance() { var ctx = useContext(PageRenderContext); var _a = useState(function () { return ctx.AddPage(); }), page = _a[0], setPage = _a[1]; var lastPageRef = useSyncedRef(page); var addPage = useSyncedRef(ctx.AddPage); useEffect(function () { lastPageRef.current.remove(); var pg = addPage.current(); setPage(pg); return function () { pg.remove(); }; }, [lastPageRef, addPage]); return page; }
var Page = /** @class */ (function () {
    function Page(id, onRemove, onRender) {
        this.id = void 0;
        this.rendering = true; // is in the middle of rendering or has a pending render
        this.stage = "over-commit";
        this.lastGoodToeLineQty = -1;
        this.lastActualHeight = -1;
        this.lastMaxAllowed = -1;
        this.onRemove = void 0;
        this.onRender = void 0;
        this.id = id;
        this.onRemove = onRemove;
        this.onRender = onRender;
        this.reset();
    }
    Page.prototype.reset = function () { this.rendering = true; this.stage = "over-commit"; this.lastGoodToeLineQty = -1; this.lastActualHeight = -1; this.lastMaxAllowed = -1; };
    Page.prototype.renderStart = function () { this.rendering = true; };
    Page.prototype.renderComplete = function (actualHeight, maxAllowed) { this.rendering = false; this.lastActualHeight = actualHeight; this.lastMaxAllowed = maxAllowed; this.onRender(); };
    Page.prototype.remove = function () { this.onRemove(); };
    return Page;
}());
var State = /*#__PURE__*/ function (State) { State[State["INIT"] = 0] = "INIT"; State[State["NEW_DATA"] = 1] = "NEW_DATA"; State[State["SMOOTHING"] = 2] = "SMOOTHING"; State[State["READY"] = 3] = "READY"; return State; }(State || {});
export function PageRenderProvider(props) {
    var pageList = useRef([]);
    var _a = useState(State.INIT), state = _a[0], setState = _a[1];
    var _b = useState([]), pageDivs = _b[0], setPageDivs = _b[1];
    var onReady = useSyncedRef(props.onReady);
    var pageIdRef = useRef(0);
    var pagesUpdated = useMemo(function () { return new EventEmitter(); }, []);
    var propsOnReady = props.onReady;
    useEffect(function () { if (state === State.READY) {
        propsOnReady(true);
    } }, [propsOnReady, state]);
    var debugKeyRef = useSyncedRef(props.debugKey || "ndbg");
    useEffect(function () { if (state === State.READY)
        return; var int = setInterval(function () { console.log("page-context", debugKeyRef.current, "still calculating"); }, 10 * 1000); return function () { return clearInterval(int); }; }, [state, debugKeyRef]);
    var ctx = useMemo(function () { return { AddPage: function () { var p = new Page(pageIdRef.current++, function () { console.log("page-context", debugKeyRef.current, p.id, "page removed"); pageList.current = pageList.current.filter(function (l) { return l !== p; }); pagesUpdated.emit(null); }, function () { return pagesUpdated.emit(null); }); console.log("page-context", debugKeyRef.current, p.id, "page created"); pageList.current.push(p); pagesUpdated.emit(null); return p; } }; }, [pagesUpdated, pageIdRef, debugKeyRef]);
    var divRef = useSyncedRef(pageDivs);
    var dataRef = useSyncedRef(props.data);
    var prefKeyRef = useRef(props.preferCombinedKey);
    prefKeyRef.current = props.preferCombinedKey;
    useEffect(function () { console.log("page-context", debugKeyRef.current, "got new data, ready=false"); pageList.current.map(function (p) { return p.reset(); }); setState(State.SMOOTHING); setPageDivs([{ startIndex: 0 }]); onReady.current(false); }, [props.data, onReady, debugKeyRef]);
    var stateRef = useSyncedRef(state);
    useEffect(function () {
        var sub = pagesUpdated.subscribeAndFireLast(function () {
            if (stateRef.current !== State.SMOOTHING)
                return;
            var divs = divRef.current;
            var updateDivs = [];
            function pageDataLength(pageIndex) { var start = divs[pageIndex].startIndex; if (divs[pageIndex + 1]) {
                return divs[pageIndex + 1].startIndex - start;
            } return dataRef.current.length - start; }
            function getLastDataItem(pageIndex) { var start = divs[pageIndex].startIndex; return dataRef.current[start + pageDataLength(pageIndex) - 1]; }
            function pullbackCalcDataLength(pageIndex) {
                var start = divs[pageIndex].startIndex;
                var end = divs[pageIndex + 1] ? divs[pageIndex + 1].startIndex : dataRef.current.length; // half the data
                var length = Math.floor((end - start) / 2);
                if (length < 1)
                    return 1; // aim for 100 or less (rule of thumb for how many rows on a page)
                if (length > 100) {
                    length = 100;
                } // rounding by splitKey
                var prefKey = prefKeyRef.current;
                if (!prefKey)
                    return length;
                var key = prefKey(dataRef.current[start + length]);
                if (!key)
                    return length; // explore lower half of data (try to pullback slowly)
                var lastI = null;
                for (var i = length; i > 0; i--) {
                    if (prefKey(dataRef.current[start + i]) !== key) {
                        if (lastI === null)
                            break;
                        return lastI;
                    }
                    lastI = i;
                } // explore upper half of data
                var half = length;
                lastI = null;
                for (var i = 0; i < length; i++) {
                    if (prefKey(dataRef.current[start + half + i]) !== key) {
                        if (lastI === null)
                            break;
                        return lastI;
                    }
                    lastI = i + half;
                } // have to split the pref key
                return length;
            }
            function toeLineCalcDataLength(pageIndex) {
                var start = divs[pageIndex].startIndex;
                var end = divs[pageIndex + 1] ? divs[pageIndex + 1].startIndex : dataRef.current.length;
                var maxLength = dataRef.current.length - start - 1;
                var oldLength = end - start; // increment by 1, rounding by prefKey
                var newLength = oldLength + 1;
                if (newLength > maxLength)
                    return maxLength;
                var prefKey = prefKeyRef.current;
                if (!prefKey)
                    return newLength;
                var key = prefKey(dataRef.current[start + oldLength]);
                for (var i = newLength; i <= maxLength; i++) {
                    if (prefKey(dataRef.current[start + i]) !== key) {
                        return i;
                    }
                }
                return maxLength;
            }
            if (pageList.current.length === 0)
                return;
            console.log("page-context", debugKeyRef.current, pageList.current, divs);
            for (var i = 0; i < pageList.current.length; i++) {
                var pg = pageList.current[i]; // ignore locked pages
                if (pg.stage === "locked") {
                    console.log("page-context", debugKeyRef.current, pg.id, "locked");
                    updateDivs.push(divs[i]);
                    if (i + 1 === pageList.current.length) {
                        if (divs.length === pageList.current.length) { // done
                            console.log("page-context", debugKeyRef.current, "everything locked, onReady(true)");
                            setState(State.READY);
                        }
                        else {
                            console.log("page-context", debugKeyRef.current, "everything locked, missing pages/divs");
                        } // don't update divs if we're at the end and everything is locked
                        return;
                    }
                    continue;
                } // nothing to do while we wait for it to render
                if (pg.rendering) {
                    console.log("page-context", debugKeyRef.current, pg.id, "rendering");
                    return;
                }
                if (pg.stage === "over-commit") {
                    pg.stage = "pull-back";
                    console.log("page-context", debugKeyRef.current, pg.id, "over-commit"); // done, it fits and it's the last page
                    if (pg.lastActualHeight <= pg.lastMaxAllowed) {
                        pg.stage = "locked";
                        console.log("page-context", debugKeyRef.current, pg.id, "locked2");
                        if (i + 1 === pageList.current.length) {
                            if (divs.length === pageList.current.length) { // done
                                console.log("page-context", debugKeyRef.current, "everything locked, onReady(true)");
                                setState(State.READY);
                            }
                            else {
                                console.log("page-context", debugKeyRef.current, "everything locked, missing pages/divs");
                            } // don't update divs if we're at the end and everything is locked
                            return;
                        }
                        continue;
                    } // overflowed, pull-back by halving the data and re-rendering
                    var newLength = pullbackCalcDataLength(i);
                    if (newLength !== pageDataLength(i)) {
                        updateDivs.push(divs[i]);
                        updateDivs.push({ startIndex: divs[i].startIndex + newLength });
                        console.log("page-context", debugKeyRef.current, pg.id, "update-length", newLength);
                    }
                    else if (newLength === 1) { // if there's only 1 row, lock it in, we can't reduce further
                        pg.stage = "locked";
                        console.log("page-context", debugKeyRef.current, pg.id, "locked3");
                        if (i + 1 === pageList.current.length) {
                            if (divs.length === pageList.current.length) { // done
                                console.log("page-context", debugKeyRef.current, "everything locked, onReady(true)");
                                setState(State.READY);
                            }
                            else {
                                console.log("page-context", debugKeyRef.current, "everything locked, missing pages/divs");
                            } // don't update divs if we're at the end and everything is locked
                            return;
                        }
                        continue;
                    }
                    pg.rendering = true;
                    break;
                }
                if (pg.stage === "pull-back") {
                    console.log("page-context", debugKeyRef.current, pg.id, "pull-back");
                    if (pg.lastActualHeight > pg.lastMaxAllowed) { // overflowed, pull-back by halving the data and re-rendering
                        var newLength_1 = pullbackCalcDataLength(i);
                        if (newLength_1 !== pageDataLength(i)) {
                            console.log("page-context", debugKeyRef.current, pg.id, "update-length2", newLength_1);
                            updateDivs.push(divs[i]);
                            updateDivs.push({ startIndex: divs[i].startIndex + newLength_1 });
                            pg.rendering = true;
                            break;
                        }
                    }
                    pg.stage = "toe-line-over";
                    pg.lastGoodToeLineQty = pageDataLength(i);
                    var newLength = toeLineCalcDataLength(i);
                    console.log("page-context", debugKeyRef.current, pg.id, "toe-line-over", pg.lastGoodToeLineQty);
                    if (newLength !== pageDataLength(i)) {
                        console.log("page-context", debugKeyRef.current, pg.id, "update-length3", newLength); // increment by 1 until we overflow
                        updateDivs.push(divs[i]);
                        updateDivs.push({ startIndex: divs[i].startIndex + newLength });
                        pg.rendering = true;
                        break;
                    }
                }
                if (pg.stage === "toe-line-over") {
                    console.log("page-context", debugKeyRef.current, pg.id, "toe-line-over", "have", pageDataLength(i), "last-good", pg.lastGoodToeLineQty, getLastDataItem(i));
                    if (pageDataLength(i) === 1) {
                        console.log("page-context", debugKeyRef.current, pg.id, "locked-1");
                        pg.stage = "locked";
                        continue;
                    }
                    if (pg.lastActualHeight > pg.lastMaxAllowed) {
                        console.log("page-context", debugKeyRef.current, pg.id, "locked-revert"); // overflowed, pull-back by one, this is done
                        updateDivs.push(divs[i]);
                        updateDivs.push({ startIndex: divs[i].startIndex + pg.lastGoodToeLineQty });
                        pg.stage = "locked";
                        break;
                    }
                    pg.lastGoodToeLineQty = pageDataLength(i); // increment by until we overflow
                    var newLength = toeLineCalcDataLength(i); // at end of data, locked, done
                    if (newLength === pageDataLength(i)) {
                        console.log("page-context", debugKeyRef.current, pg.id, "locked-end-data");
                        pg.stage = "locked";
                        continue;
                    }
                    console.log("page-context", debugKeyRef.current, pg.id, "new-length3", newLength);
                    updateDivs.push(divs[i]);
                    updateDivs.push({ startIndex: divs[i].startIndex + newLength });
                    pg.rendering = true;
                    break;
                }
                throw new Error("unexpected stage ".concat(pg.stage, " ").concat(pg.id));
            }
            if (updateDivs.length > 0) {
                console.log("page-context", debugKeyRef.current, "update divs", updateDivs);
                setPageDivs(updateDivs);
                divRef.current = updateDivs; // update ref just in-case there's another sub-render before it's updated
            }
        });
        return function () { return sub.cancel(); };
    }, [dataRef, divRef, pagesUpdated, stateRef, debugKeyRef]);
    return /*#__PURE__*/ _jsx(PageRenderContext.Provider, { value: ctx, children: /*#__PURE__*/ _jsx(PageRenderCtrl, _objectSpread(_objectSpread({}, props), {}, { pageDivs: pageDivs })) });
}
function PageRenderCtrl(props) { var sections = useMemo(function () { var list = []; for (var i = 0; i < props.pageDivs.length; i++) {
    var start = props.pageDivs[i].startIndex;
    if (i + 1 < props.pageDivs.length) {
        var end = props.pageDivs[i + 1].startIndex;
        list.push({ startingDataIndex: start, pageIndex: i, rows: props.data.slice(start, end), isLastPage: false });
    }
    else {
        list.push({ startingDataIndex: start, pageIndex: i, rows: props.data.slice(start), isLastPage: true });
    }
} return list; }, [props.data, props.pageDivs]); console.log("page-render", props.pageDivs.map(function (i) { return i.startIndex; }).join(","), sections.map(function (s) { return "length: " + s.rows.length; })); return /*#__PURE__*/ _jsx(_Fragment, { children: sections.map(function (sect, index) { /*#__PURE__*/ return _jsx(React.Fragment, { children: props.children(sect) }, index); }) }); }
